import React, { Dispatch, FC, ReactNode, SetStateAction, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Alert, Keyboard, Platform, StyleProp, StyleSheet, ViewStyle } from "react-native"
import { Image, Options } from "react-native-image-crop-picker"
// Pour Android 13+ Photo Picker
import { launchImageLibrary } from "react-native-image-picker"
import { PERMISSIONS } from "react-native-permissions"

import { ButtonType, useAlert } from "../../features/Providers"
import useTheme from "../../features/Theme/ThemeProvider"
import { askNativePermissions } from "../../utils/askNativePermission"
import generateShadow from "../../utils/generateShadow"
import { DocumentPickerResponse, useGalleryPicker } from "../GalleryPicker"
import { Touchable } from "../Touchable"
import { VectorIconProps } from "../VectorIcon"
import PermissionDialogContent from "./PermissionDialogContent"

export type File = Blob & { name: string; type: string }
export type Fileish = File | Image | DocumentPickerResponse

export interface IProps {
  disabled?: boolean
  children: ReactNode
  imageOptions?: Options
  downloadFile?: boolean
  downloadGallery?: boolean
  style?: StyleProp<ViewStyle>
  setSource:
    | Dispatch<SetStateAction<Fileish | undefined>>
    | ((file: Fileish | Image[] | DocumentPickerResponse[] | undefined) => void)
  icons?: { camera?: VectorIconProps; gallery?: VectorIconProps; documents?: VectorIconProps }
}

const PermissionCameraWrapper: FC<IProps> = ({
  disabled,
  children,
  style,
  setSource,
  imageOptions,
  icons,
  downloadFile = false,
  downloadGallery = true,
}) => {
  const {
    typography: { h6 },
    dimensions: { spacing },
    colors: { white, primary },
  } = useTheme()
  const { t } = useTranslation()
  const { showDialog, onDismiss } = useAlert()
  const { openCamera, openDocumentGallery, openGallery, source, clearCamera } = useGalleryPicker()

  const s = useMemo(
    () => ({
      view: [styles.view, { paddingHorizontal: spacing }],
      button: [{ backgroundColor: white.highEmphasis }, generateShadow(1)],
    }),
    [spacing, white.highEmphasis],
  )

  useEffect(() => {
    if (source) {
      setSource(source)
      clearCamera()
    }
  }, [source, setSource, clearCamera])

  const pickGalleryAndroid13Plus = async () => {
    try {
      const result = await launchImageLibrary({
        mediaType: "photo",
      })

      if (result.didCancel) {
        return
      }

      if (result.errorCode) {
        Alert.alert(t("permission.error"), result.errorMessage || t("permission.gallery_failed"))
        return
      }

      if (result.assets && result.assets.length > 0) {
        const asset = result.assets[0]

        // Adapter le format de react-native-image-picker au format attendu par l'app
        // Firebase Storage sur Android accepte les content:// URIs
        const adaptedImage: Image = {
          path: asset.uri || "",
          mime: asset.type || "image/jpeg",
          width: asset.width || 0,
          height: asset.height || 0,
          size: asset.fileSize || 0,
          filename: asset.fileName || undefined,
        }

        setSource(adaptedImage)
      }
    } catch (e) {
      Alert.alert(t("permission.error"), t("permission.gallery_failed"))
    }
  }

  const onPress = async () => {
    Keyboard.dismiss()
    const platformVersion =
      typeof Platform.Version === "string" ? parseInt(Platform.Version) : Platform.Version

    // Permissions
    const permissionsArray = Platform.select({
      android:
        platformVersion >= 33
          ? [PERMISSIONS.ANDROID.CAMERA]
          : [PERMISSIONS.ANDROID.CAMERA, PERMISSIONS.ANDROID.READ_EXTERNAL_STORAGE],
      ios: [PERMISSIONS.IOS.CAMERA, PERMISSIONS.IOS.PHOTO_LIBRARY],
      default: [],
    })

    const result = await askNativePermissions(
      permissionsArray,
      "",
      t("permission.message"),
      t("button.ok"),
      t("button.noThanks"),
    )

    if (result !== "granted") {
      return
    }
    showDialog({
      type: "button",
      title: t("alert.photo.title"),
      titleStyle: h6,
      style: s.view,
      contentContainerStyle: styles.modal,
      positive: {
        type: ButtonType.NEGATIVE,
        label: t("button.cancel"),
        buttonStyle: s.button,
      },
      children: (
        <PermissionDialogContent
          {...{ icons }}
          openCamera={async () => {
            await openCamera(imageOptions)
            onDismiss()
          }}
          openGallery={
            downloadGallery
              ? async () => {
                  if (Platform.OS === "android" && platformVersion >= 33) {
                    await pickGalleryAndroid13Plus()
                  } else {
                    await openGallery(imageOptions)
                  }
                  onDismiss()
                }
              : undefined
          }
          openDocuments={
            downloadFile
              ? async () => {
                  await openDocumentGallery(["*/*"])
                  onDismiss()
                }
              : undefined
          }
        />
      ),
    })
  }

  return (
    <Touchable borderless rippleColor={primary} {...{ style, onPress, disabled }}>
      {children}
    </Touchable>
  )
}

const styles = StyleSheet.create({
  view: { alignSelf: "center" },
  modal: { width: "100%", maxWidth: 420, alignSelf: "center" },
})

export default PermissionCameraWrapper
